import { Controller } from 'stimulus';
import serializeFormToObject from '../helpers/serializeFormToObject';
import delegatedEventListener from '../helpers/delegatedEventListener';
import hasHistorySupport from '../helpers/hasHistorySupport';
import {
  getLabelTextForInput,
  getInputForKeyAndValue,
  getDefaultForInput,
  renderSelectedFilter,
  deleteFilterForButton,
} from '../helpers/filterHelpers';

class FilterController extends Controller {
  connect() {
    this.outputSelectedFilters(this.getSelectedFilters());
    this.toggleClearButton();

    this.groupTargets.forEach((groupTarget) => {
      delegatedEventListener('change', 'input[type="checkbox"], input[type="radio"]', groupTarget, () => {
        this.onFilterChange();
      });
    });
  }

  onFilterChange() {
    const formData = serializeFormToObject(this.formTarget);

    this.fetchPage(formData)
      .then((res) => {
        if (typeof res.products === 'undefined') {
          throw new Error('Products are missing');
        }

        this.outputProducts(res.products);

        if (typeof res.results !== 'undefined') {
          this.outputResults(res.results);
        }
      });
  }

  fetchPage(formData) {
    return new Promise((resolve, reject) => {
      const formUrl = this.formTarget.getAttribute('action');

      $.ajax({
        url: formUrl,
        data: formData,
        success: resolve,
        error: reject,
      });
    });
  }

  outputProducts(products) {
    this.runOffMainThread(() => {
      this.productsTarget.innerHTML = products;

      this.outputSelectedFilters(this.getSelectedFilters());
      this.toggleClearButton();
      this.recreateMasonry();
    });
  }

  recreateMasonry() {
    const masonryControllerEl = document.querySelector('[data-controller*="masonry"]');
    const masonryController = this.application.getControllerForElementAndIdentifier(
      masonryControllerEl,
      'masonry',
    );

    masonryController.recreateColumns();
  }

  // eslint-disable-next-line class-methods-use-this
  runOffMainThread(cb) {
    if ('requestAnimationFrame' in window) {
      requestAnimationFrame(() => {
        requestAnimationFrame(cb);
      });
      return;
    }

    setTimeout(cb);
  }

  // eslint-disable-next-line class-methods-use-this
  outputUrl(url) {
    if (!hasHistorySupport()) {
      return;
    }

    if (url.indexOf('http') === 0) {
      window.history.replaceState(null, null, url);
      return;
    }

    window.history.replaceState(null, null, `?${url}`);
  }

  outputResults(results) {
    const resultsLabel = results === 1 ? app.i18n.product : app.i18n.products;
    this.resultsTarget.innerHTML = `${results} ${resultsLabel}`;
  }

  onSortChange() {
    this.onFilterChange();
  }

  onConfirmClick() {
    this.closeAllDropdowns();
  }

  onShowResults() {
    this.closeFilter();
  }

  closeFilter() {
    $(this.element.querySelector('#filter')).collapse('hide');
  }

  onWindowClick(event) {
    const { target } = event;
    const sectionElement = target.closest('.filter__section');

    if (!sectionElement) {
      this.closeAllDropdowns();
    }
  }

  closeAllDropdowns() {
    const dropdownNodes = this.element.querySelectorAll('.filter__section-collapse');
    const dropdowns = Array.prototype.slice.call(dropdownNodes);

    dropdowns.forEach((dropdown) => {
      $(dropdown).collapse('hide');
    });
  }

  onDeleteFilter(e) {
    deleteFilterForButton(e.target.closest('button'));
    this.onFilterChange();
  }

  onDeleteFilters() {
    this.selectedDeleteButtonsTargets.forEach(deleteFilterForButton);
    this.onFilterChange();
  }

  getSelectedFilters() {
    const selectedValues = serializeFormToObject(this.formTarget);
    const selectedItems = [];

    $.each(selectedValues, (key, val) => {
      if (!val) {
        return;
      }

      if (key === 'sort') {
        return;
      }

      const label = getLabelTextForInput(key, val);
      const input = getInputForKeyAndValue(key, val);
      const defaultValue = getDefaultForInput(input);

      if (input.value === defaultValue || !label) {
        return;
      }

      selectedItems.push({
        key,
        value: val,
        label,
      });
    });

    return selectedItems;
  }

  outputSelectedFilters(filters) {
    this.selectedTarget.innerHTML = filters.map(renderSelectedFilter).join('');

    this.selectedTarget.style.display = filters.length > 0 ? 'block' : 'none';
  }

  filter() {
    this.emptyResults();
    this.outputSelectedFilters(this.getSelectedFilters());

    const parameters = $(this.formTarget).serialize();
    const canonical = document.querySelector('link[rel="canonical"]').href;

    if (hasHistorySupport()) {
      const url = `${canonical}${parameters ? '?' : ''}${parameters}`;
      try {
        window.history.replaceState(null, null, url);
      } catch (error) {
        //
      }
    }
  }

  emptyResults() {
    this.resultsTarget.innerHTML = '';
  }

  toggleSpinner(state) {
    this.spinnerTarget.style.display = state ? 'block' : 'none';
  }

  toggleClearButton() {
    this.clearButtonTarget.style.display = this.getSelectedFilters().length > 0 ? 'block' : 'none';
  }
}

FilterController.targets = [
  'form',
  'selected',
  'selectedDeleteButtons',
  'spinner',
  'results',
  'resultsCount',
  'clearButton',
  'sortSelect',
  'products',
  'group',
];

export default FilterController;
